import { useQuery } from '@tanstack/react-query';
import type { WorkspaceEntitlementsDto } from '@aiai/shared';
import {
  fetchSettingsStatus,
  fetchWorkspaceEntitlements,
  type SettingsStatus,
} from '../../api';
import { qk } from '../../api/queryKeys';
import { Badge } from '../../components/Badge';
import { LoadingState, ErrorState } from '../../components/States';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useOrg } from '../../org/useOrg';
import { SettingsSection, SecretField, type SecretSpec } from './common';

const AGENT_SECRET: SecretSpec = {
  key: 'CLAUDE_CODE_OAUTH_TOKEN',
  title: 'Claude subscription token',
  description:
    'Generate once with `claude setup-token` (needs Pro/Max/Team/Enterprise). Agents use your subscription — flat-rate, not metered API billing. This is the only supported auth mode: every run needs it.',
  placeholder: 'sk-ant-oat…',
  required: true,
  removeConsequence: 'Future runs will fail until a new one is set.',
};

const GITHUB_SECRET: SecretSpec = {
  key: 'GITHUB_TOKEN',
  title: 'GitHub token',
  description:
    'Non-prototype projects work with a classic `repo` PAT or a fine-grained PAT (contents:write, pull_requests:write). Prototypes require a classic PAT with `repo` + `delete_repo` — fine-grained tokens cannot create or delete repositories. Runs continue without it, but clone/push/PR will fail.',
  placeholder: 'github_pat_…',
  removeConsequence:
    'Clone, push, PR creation, and the GitHub Issues backend will fail until a new one is set.',
};

export function GeneralSettingsSection() {
  useDocumentTitle('Settings · General');
  const { activeOrg } = useOrg();
  const { data, isLoading, isError, refetch } = useQuery<SettingsStatus>({
    queryKey: qk.settings,
    queryFn: fetchSettingsStatus,
  });
  const { data: entitlements } = useQuery<WorkspaceEntitlementsDto>({
    queryKey: ['orgEntitlements', activeOrg?.id],
    queryFn: () => fetchWorkspaceEntitlements(activeOrg!.id),
    enabled: Boolean(activeOrg?.id),
  });

  if (isLoading) return <LoadingState label="Loading settings…" />;
  if (isError) return <ErrorState message="Could not load settings" onRetry={() => refetch()} />;

  const claudeConfigured = Boolean(data?.[AGENT_SECRET.key]);
  const platformAvailable = Boolean(data?.platformClaudeTokenAvailable);

  // On the shared credential rail (basic plan) the platform provides Claude and
  // GitHub; the workspace has no BYO tokens to manage, and letting it set its
  // own Claude token would sidestep the plan's spend cap. Hide the fields and
  // explain where credentials come from instead.
  if (entitlements?.credentialMode === 'shared') {
    return (
      <div className="space-y-6">
        <SettingsSection
          title="Credentials"
          description="How agents authenticate with Claude and GitHub, for the current workspace."
        >
          <div className="flex items-start gap-2 rounded-md border border-border bg-surface-muted px-3 py-2 text-sm text-ink-muted">
            <Badge tone="info" size="sm">Platform-managed</Badge>
            <span>
              This workspace runs on the <strong>{entitlements.plan}</strong> plan, which uses
              platform-managed credentials. Claude and GitHub are provided by the platform —
              there are no tokens to configure here.
            </span>
          </div>
        </SettingsSection>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Agent"
        description="How agents authenticate with Claude, for the current workspace."
      >
        {!claudeConfigured && platformAvailable && (
          <div className="flex items-center gap-2 rounded-md border border-border bg-surface-muted px-3 py-2 text-xs text-ink-muted">
            <Badge tone="info" size="sm">Using platform token</Badge>
            <span>Runs use the shared Claude subscription configured by your administrator. Add your own token below to use a separate seat.</span>
          </div>
        )}
        <SecretField
          spec={AGENT_SECRET}
          configured={claudeConfigured}
        />
      </SettingsSection>

      <SettingsSection
        title="Repository (GitHub)"
        description="Access to this workspace's repos via GitHub."
      >
        <SecretField
          spec={GITHUB_SECRET}
          configured={Boolean(data?.[GITHUB_SECRET.key])}
        />
      </SettingsSection>
    </div>
  );
}
